import React, { useEffect, useMemo, useState } from "react";
import { View, StyleSheet } from "react-native";
import CustomText from "../components/common/general/CustomText/CustomText";
import { useRoute, useNavigation } from "@react-navigation/native";
import { RouteProp } from "@react-navigation/native";
import type { RootStackParamList } from "../navigation/navigation.types";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import CustomButton from "../components/common/general/CustomButton/CustomButton";
import ChatContainer from "../containers/chat/ChatContainer/ChatContainer";
import ChatBarContainer from "../containers/navigation/ChatBarContainer/ChatBarContainer";
import LearningGoalsContainer, { Objective } from "../containers/companion/LearningGoalsContainer";
import {
  useGetChatByObjectIdQuery,
  useGetCourseChatResourcesQuery,
  useGetAiTutorVideosQuery,
  useGetAiTutorQuery,
} from "../graphql/generated/graphql";
import { Context, useChatBar, ConversationType } from "../contexts/ChatBarContext";
import { useAppState, actions } from "../contexts/AppStateContext";
import useWebSocket from "../hooks/useWebSocket";
import UpgradeModalContainer from "../containers/general/UpgradeModalContainer/UpgradeModalContainer";
import { sortResourceItemsOfModules } from "../utils/sortResourceItemsOfModules";
import BlankLayout from "../layouts/BlankLayout";
import { shadowStyles } from "../styles/shadow";
import { colors, radii, spacing, typographyStyles } from "../styles/design-system";
import LottieView from "lottie-react-native";
import LottieUrl from "../assets/lottie/aiTutorChatLoadingLottie.json";
import { getValidAccessToken } from "../utils/authRefresh";
import ExplorationMenu, { ExplorationMediaItem } from "../components/learning-activity/ExplorationMenu/ExplorationMenu";

const TIMER_DURATION = 1500;

const returnRandomIntroText = () => {
  const intros = [
    "Analyzing your progress and tailoring the conversation...",
    "The more you lean into learning, the faster you'll progress through your objectives.",
    "We detect when answers are copied from AI or other sources - please be original in your responses.",
    "Ask questions when you're curious! The AI loves when you dig deeper into topics.",
    "Don't worry about making mistakes - they're part of the learning process and help you grow.",
    "Take your time to think through each question. Quality of thought matters more than speed.",
    "Your previous conversations help the AI understand your learning style - each session gets more personalized.",
    "Try explaining concepts in your own words - it helps solidify your understanding.",
    "The AI remembers where you've struggled before and will help you strengthen those areas.",
    "Engage authentically - we can tell when responses aren't genuinely your own thinking.",
    "Every question is an opportunity to deepen your understanding of the subject.",
    "Your learning journey is unique - no two students will have the same experience here.",
    "Think critically about each concept before responding - deeper thinking leads to better learning.",
    "Feel free to ask for clarification if something doesn't make sense.",
    "The more engaged you are in the conversation, the more the AI can help you learn.",
    "Building on your past conversations to create the perfect learning experience for you.",
    "Quality responses help the AI understand your thought process and adapt accordingly.",
    "Each interaction helps build a more personalized and effective learning path.",
    "Curiosity and genuine engagement are the keys to accelerating your progress.",
    "Preparing an adaptive conversation based on your unique learning patterns...",
  ];
  return intros[Math.floor(Math.random() * intros.length)];
};

const CustomCourseAITutorChatView = () => {
  const route = useRoute<RouteProp<RootStackParamList, "Custom Course AITutor">>();
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const { resource_id, course_id, integration_id, resource_type, module_id } = route.params;
  const { sendMessage } = useWebSocket();
  const {
    setContext,
    setActiveContextAndObjectId,
    setConversationType,
    setSchoolMessage,
    mapRouteToContext,
    setMetaIsLoading,
    state: chatBarState,
  } = useChatBar();
  const [upgradeModalVisible, setUpgradeModalVisible] = useState(false);
  const [isBackHovered, setIsBackHovered] = useState(false);
  const { state: appState, dispatch } = useAppState();

  const [showIntro, setShowIntro] = useState(true);
  const [minLoadingTimePassed, setMinLoadingTimePassed] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setMinLoadingTimePassed(true), TIMER_DURATION);
    return () => clearTimeout(timer);
  }, []);

  const { data: courseData } = useGetCourseChatResourcesQuery({
    variables: { id: course_id },
    skip: !course_id,
  });

  const { data: aiTutorData, loading: aiTutorLoading } = useGetAiTutorQuery({
    variables: { id: resource_id },
    skip: !resource_id,
  });

  const {
    data: chatByObjectIdData,
    loading: chatByObjectIdLoading,
    refetch: chatByObjectIdRefetch,
  } = useGetChatByObjectIdQuery({
    variables: {
      objectId: resource_id,
      context: Context.AITUTOR,
    },
    skip: !resource_id,
    fetchPolicy: "network-only",
  });

  const { data: aiTutorVideosData, refetch: refetchAiTutorVideos } = useGetAiTutorVideosQuery({
    variables: { id: resource_id },
    skip: !resource_id,
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    if (chatBarState.activeObjectId) {
      chatByObjectIdRefetch({
        objectId: chatBarState.activeObjectId,
        context: chatBarState.activeContext,
      });
    }
  }, [appState.meta.chatHistory]);

  const handleGetHelpFromAxio = async (id: string) => {
    const token = await getValidAccessToken();

    sendMessage({
      meta: {
        token: token,
        abort: false,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        isLoading: true,
        displayMessage: false,
      },
      state: {
        general: "SCHOOL",
        context: mapRouteToContext("Custom Resource Student"),
        params: {
          resource_type: "AITutor",
          resource_id: id,
          course_id,
          integration_id,
        },
        school: {
          contentType: "AITutor",
          contentId: id,
          classId: course_id,
          integrationId: integration_id,
        },
      },
      content: {
        message: chatByObjectIdData?.getChatByObjectId?.id
          ? "Let's continue where we left off"
          : "Please begin by introducing me to this topic",
        author: "HUMAN",
        type: null,
      },
    });
    setMetaIsLoading(true);
  };

  useEffect(() => {
    if (chatByObjectIdLoading || chatBarState.llmMessage.meta.isLoading) {
      return;
    }

    if (!chatByObjectIdData?.getChatByObjectId?.id) {
      handleGetHelpFromAxio(resource_id);
    } else {
      if (minLoadingTimePassed) {
        setShowIntro(false);
      }
    }
  }, [chatByObjectIdData, chatByObjectIdLoading, chatBarState.llmMessage.meta.isLoading, minLoadingTimePassed]);

  useEffect(() => {
    setConversationType(ConversationType.SCHOOL);
    setActiveContextAndObjectId({
      context: Context.AITUTOR,
      objectId: resource_id,
    });
    setContext({
      context: Context.AITUTOR,
      params: {
        resource_id: resource_id,
        course_id,
        integration_id,
        resource_type: "AITutor",
      },
    });
    setSchoolMessage({
      contentType: resource_type,
      contentId: resource_id,
      classId: course_id,
      integrationId: integration_id,
    });
  }, [resource_id, course_id, integration_id, resource_type]);

  const handleExitPress = () => {
    dispatch({
      type: actions.SET_CONTENT,
      payload: { message: "" },
    });
    if (navigation.canGoBack()) {
      navigation.goBack();
    } else {
      const moduleId = route.params?.module_id;
      if (moduleId && typeof moduleId === "string") {
        const integrationId =
          typeof route.params?.integration_id === "string" ? route.params.integration_id : course_id;
        navigation.navigate("Custom Module Student", {
          module_id: moduleId,
          course_id: course_id,
          integration_id: integrationId,
        });
      } else {
        navigation.navigate("Custom Course Student", {
          course_id: course_id,
          integration_id,
        });
      }
    }
  };

  const handleCloseUpgradeModals = () => {
    setUpgradeModalVisible(false);
  };

  const formattedModules = useMemo(() => {
    return courseData?.getCourseChatResources.data[0].modules.map((module) => {
      const aiTutors = module.aiTutors.map((aiTutor) => ({
        ...aiTutor,
        resourceType: "aiTutor",
      }));

      const axioResources = module.axioResources.map((resource) => ({
        ...resource,
        resourceType: "axioResource",
      }));

      const assignments = module.assignments.map((assignment) => ({
        ...assignment,
        resourceType: "assignment",
      }));

      const assessments = module.assessment ? [{ ...module.assessment, resourceType: "assessment" }] : [];

      const projects = module.projects.map((project) => ({
        ...project,
        resourceType: "project",
      }));

      const allResources = [...aiTutors, ...axioResources, ...assignments, ...assessments, ...projects];

      return {
        moduleId: module.id,
        resources: sortResourceItemsOfModules(allResources, module.resourceOrder),
      };
    });
  }, [courseData]);

  const explorationItems = useMemo<ExplorationMediaItem[]>(() => {
    const videos = aiTutorVideosData?.getAITutorVideos?.data ?? [];
    return videos.map((video) => ({
      id: video.id,
      title: video.title,
      type: "video",
      metadata: "Suggested",
      thumbnail:
        video.thumbnailUrl || (video.videoId ? `https://img.youtube.com/vi/${video.videoId}/hqdefault.jpg` : ""),
      videoId: video.videoId,
      description: video.description ?? undefined,
      mediaUrl: video.url ?? undefined,
    }));
  }, [aiTutorVideosData]);

  const handleExplorationOpen = () => {
    if (!resource_id) {
      return;
    }
    refetchAiTutorVideos();
  };

  useEffect(() => {
    if (appState.meta.isAtDayLimit || appState.meta.isAtLifetimeLimit) {
      setUpgradeModalVisible(true);
    }
  }, [appState.meta.isAtDayLimit, appState.meta.isAtLifetimeLimit]);

  const selectedResource = useMemo(() => {
    const module = formattedModules?.find((resourceModule) =>
      resourceModule.resources.some((resource) => resource.id === resource_id)
    );
    return module?.resources.find((resource) => resource.id === resource_id);
  }, [formattedModules, resource_id]);

  return (
    <BlankLayout>
      <View style={styles.container}>
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.headerLeft}>
            <CustomButton
              onPress={handleExitPress}
              size="s"
              text="Back to course"
              styleType="secondary"
              onHover={(hovered) => setIsBackHovered(hovered)}
              style={[styles.backButton, isBackHovered && styles.backButtonHover]}
              textStyle={styles.backButtonText}
              accessibilityLabel="Exit button"
            />
            {/* Display the course name */}
            <CustomText
              text={selectedResource?.title}
              useTranslationText={false}
              weight="bold"
              size="m"
              accessibilityLabel="Selected resource title"
              aria-level="1"
              role="heading"
              style={styles.headerTitle}
            />
          </View>
        </View>

        {/* Main Content */}
        <View style={styles.content}>
          {/* Chat Area */}
          <View style={styles.chatArea}>
            {!aiTutorLoading ? (
              <LearningGoalsContainer
                objectives={
                  (aiTutorData?.getAITutor?.data?.[0]?.objectives as Objective[] | undefined) ?? ([] as Objective[])
                }
              />
            ) : null}
            <View style={styles.chatInner}>
              {showIntro ? (
                <View style={{ flex: 1, justifyContent: "center", alignItems: "center" }}>
                  <View style={[styles.loaderContainer, shadowStyles.allAroundShadow]}>
                    <LottieView autoPlay source={LottieUrl} speed={1} loop={true} style={{ width: 100, height: 100 }} />
                    <CustomText
                      text="Loading your personalized learning experience"
                      useTranslationText={false}
                      size="l"
                      weight="bold"
                      style={{ textAlign: "center" }}
                      aria-level="2"
                      role="heading"
                    />
                    <CustomText
                      text={returnRandomIntroText()}
                      useTranslationText={false}
                      size="m"
                      weight="regular"
                      style={{ marginTop: 10, textAlign: "center" }}
                    />
                  </View>
                </View>
              ) : (
                <ChatContainer chatData={chatByObjectIdData?.getChatByObjectId} />
              )}
            </View>
            <ChatBarContainer />
          </View>
          <ExplorationMenu items={explorationItems} onOpen={handleExplorationOpen} />
        </View>

        <UpgradeModalContainer visible={upgradeModalVisible} onClose={handleCloseUpgradeModals} />
      </View>
    </BlankLayout>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.white,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    borderBottomColor: colors.grey[200],
    borderBottomWidth: 1,
  },
  headerLeft: {
    paddingHorizontal: spacing.lg,
    paddingVertical: 10,
    gap: spacing.sm,
    flexDirection: "row",
    alignItems: "center",
  },
  backButton: {
    minHeight: 36,
    padding: 0,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderRadius: radii.sm,
    backgroundColor: colors.grey[100],
  },
  backButtonHover: {
    backgroundColor: colors.grey[200],
  },
  backButtonText: {
    ...typographyStyles.textSm,
    ...typographyStyles.fontRegular,
    color: colors.grey[900],
  },
  headerTitle: {
    color: colors.grey[900],
  },
  content: {
    flex: 1,
    position: "relative",
    backgroundColor: colors.grey[50],
  },
  chatArea: {
    flex: 1,
    position: "relative",
  },
  chatInner: {
    flex: 1,
    width: "100%",
    alignSelf: "stretch",
    paddingTop: 0,
    paddingBottom: spacing["4xl"],
  },
  loaderContainer: {
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: colors.white,
    borderRadius: 16,
    borderWidth: 0,
    maxWidth: 500,
    width: "90%",
    padding: 40,
  },
  chatMenuContainer: {
    position: "absolute",
    top: 0,
    left: -270,
    bottom: 56,
    zIndex: 100,
  },
  menuCircle: {
    width: 28,
    height: 28,
  },
});

export default CustomCourseAITutorChatView;
